import type {
  BillingInterval,
  PlanChangePreview,
  PlanId
} from '@tandemone/types'
import { PLAN_CATALOG } from '@tandemone/types'
import { CreditCard } from 'lucide-react'

import { extractErrorMessage } from '@/api/extract-error'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

import { formatBillingDate, formatMoney, intervalLabel } from './plan-price'
import { usePaymentMethod } from './use-payment-method'
import { usePlanChangePreview } from './use-plan-change-preview'

export type PlanChoice = { planId: PlanId; interval: BillingInterval }

function cadence(interval: BillingInterval): string {
  return interval === 'year' ? 'a year' : 'a month'
}

function Summary({
  preview,
  planId
}: {
  preview: PlanChangePreview
  planId: PlanId
}) {
  const recurring = `${formatMoney(preview.recurringAmountCents, preview.currency)} ${cadence(preview.interval)}`

  if (preview.applies === 'scheduled') {
    return (
      <p className='text-[14px] text-ink'>
        Nothing to pay today. You stay on your current plan until{' '}
        {preview.effectiveAt
          ? formatBillingDate(preview.effectiveAt)
          : 'renewal'}
        , then move to {PLAN_CATALOG[planId].label} at {recurring}.
      </p>
    )
  }

  if (preview.amountDueCents === 0) {
    return (
      <p className='text-[14px] text-ink'>
        Nothing to pay today — your credit for unused time covers it. After that
        it&rsquo;s {recurring}.
      </p>
    )
  }

  const due = formatMoney(preview.amountDueCents, preview.currency)
  return (
    <div className='flex flex-col gap-1'>
      <p className='font-display text-[26px] leading-none text-ink'>
        {due} today
      </p>
      <p className='text-[13px] text-ink-soft'>
        Credit for your unused time is already taken off. Then {recurring}.
      </p>
    </div>
  )
}

/* an upgrade charges the card on confirm, so the amount and the card it lands
 * on are both named before the button that does it. */
export function PlanChangeDialog({
  choice,
  title,
  onConfirm,
  onOpenChange,
  onChangeCard,
  confirming
}: {
  choice: PlanChoice | null
  title: string
  onConfirm: (choice: PlanChoice) => void
  onOpenChange: (open: boolean) => void
  onChangeCard: () => void
  confirming: boolean
}) {
  const preview = usePlanChangePreview(choice)
  const data = preview.data
  const onCard = data?.collectionMethod === 'charge_automatically'
  const charges = data?.applies === 'immediate' && data.amountDueCents > 0
  const method = usePaymentMethod(Boolean(charges && onCard))
  const card = method.data?.method

  return (
    <Dialog open={choice !== null} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[440px]'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className='sr-only'>
            Review what this change costs before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-4'>
          {preview.isLoading && <Skeleton className='h-16 w-full' />}

          {preview.isError && (
            <p className='text-[13px] text-destructive'>
              {extractErrorMessage(preview.error)}
            </p>
          )}

          {data && choice && <Summary preview={data} planId={choice.planId} />}

          {charges && !onCard && (
            <p className='text-[13px] text-ink-soft'>
              We&rsquo;ll email the invoice. Nothing is charged automatically.
            </p>
          )}

          {charges && onCard && (
            <div className='flex flex-col items-start gap-2 rounded-lg border border-border p-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'>
              <span className='flex items-center gap-2 text-[13px] text-ink'>
                <CreditCard className='size-4 text-ink-soft' />
                {method.isLoading
                  ? 'Loading your card…'
                  : card?.last4
                    ? `Charging ${card.brand ? card.brand.toUpperCase() : 'card'} •••• ${card.last4}`
                    : 'Charging the card on file'}
              </span>
              <button
                type='button'
                onClick={onChangeCard}
                className='inline-flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-accent underline-offset-2 hover:underline sm:min-h-0'
              >
                Use a different card
              </button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!data || confirming}
            onClick={() => choice && onConfirm(choice)}
          >
            {charges && data
              ? `Confirm and pay ${formatMoney(data.amountDueCents, data.currency)}`
              : data?.applies === 'scheduled'
                ? `Schedule for ${data.effectiveAt ? formatBillingDate(data.effectiveAt) : 'renewal'}`
                : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* derived where the action is known rather than guessed from prices here. */
export function planChangeTitle(input: {
  planId: PlanId
  currentPlanId: PlanId
  interval: BillingInterval
}): string {
  if (input.planId === input.currentPlanId) {
    return `Switch to ${intervalLabel(input.interval).toLowerCase()} billing`
  }
  const up =
    PLAN_CATALOG[input.planId].monthlyPriceCents >
    PLAN_CATALOG[input.currentPlanId].monthlyPriceCents
  return `${up ? 'Upgrade' : 'Downgrade'} to ${PLAN_CATALOG[input.planId].label}`
}
